// ═══════════════════════════════════════════════════════════════════
// Aisha — AI Style Assistant v2
// Rich product context (discount prices, IDs, stock), structured JSON
// response with product cards, cart integration, direct product links.
// Avatar: animated SVG Indian woman in saree.
// ═══════════════════════════════════════════════════════════════════
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCartStore } from '../stores/cartStore';
import { toast } from 'react-toastify';
import { expandQuery, buildSynonymContext } from '../utils/aishaSynonyms';
import { cldThumb } from '../utils/cloudinary';

const GEMINI_API = 'https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash:generateContent';
const API_BASE   = process.env.REACT_APP_API_URL || 'https://api.glitzandglambyjyoti.com';

// v36 May 10 2026 — Lazy-load dotlottie web component.
// Previously loaded as <script type="module"> from CDN in public/index.html
// on every page, blocking LCP for ~1.5s on mobile even when Aisha wasn't open.
// Now: removed from index.html, loaded once via requestIdleCallback when this
// component first mounts. Until the script loads, AishaAvatar falls through
// to its diamond SVG fallback — which is also what happens if the CDN fails.
let _dotlottieLoadStarted = false;
const ensureDotlottieLoaded = () => {
  if (_dotlottieLoadStarted || typeof window === 'undefined') return;
  _dotlottieLoadStarted = true;
  const inject = () => {
    if (document.querySelector('script[data-dotlottie-wc]')) return;
    const s = document.createElement('script');
    s.src = 'https://cdn.jsdelivr.net/npm/@lottiefiles/dotlottie-wc@0.6.2/dist/dotlottie-wc.js';
    s.type = 'module';
    s.dataset.dotlottieWc = '1';
    document.head.appendChild(s);
  };
  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(inject, { timeout: 4000 });
  } else {
    setTimeout(inject, 2000);
  }
};

const QUICK_QUESTIONS = [
  { en: 'Show earrings under ₹500', hi: '₹500 से कम में झुमके दिखाओ' },
  { en: "What's on sale right now?", hi: 'अभी किन पर छूट है?' },
  { en: 'Suggest jewelry for wedding', hi: 'शादी के लिए गहने सुझाओ' },
  { en: 'Best bangles for Navratri', hi: 'नवरात्रि के लिए बेस्ट चूड़ियाँ' },
];

// ─── Aisha's avatar — Lottie animation with graceful fallback ───
// The URL is configurable from Admin → Settings. If not set or fails to load,
// falls back to an elegant diamond SVG icon. The <dotlottie-wc> web component
// is loaded via CDN in public/index.html.
const AishaAvatar = ({ size = 40, lottieUrl }) => {
  const [failed, setFailed] = useState(false);
  const url = lottieUrl && !failed ? lottieUrl : null;

  useEffect(() => { setFailed(false); }, [lottieUrl]);

  if (!url) {
    // Fallback — elegant diamond/sparkle icon, gold gradient background
    return (
      <svg width={size} height={size} viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
        <defs>
          <radialGradient id="aisha-fb-bg" cx="50%" cy="40%" r="60%">
            <stop offset="0%" stopColor="#f7e6a8"/>
            <stop offset="100%" stopColor="#c9a84c"/>
          </radialGradient>
        </defs>
        <circle cx="32" cy="32" r="32" fill="url(#aisha-fb-bg)"/>
        {/* Stylised diamond */}
        <path d="M 32 14 L 44 26 L 32 50 L 20 26 Z" fill="#fff" opacity="0.95"/>
        <path d="M 20 26 L 44 26" stroke="#8a6010" strokeWidth="0.8" fill="none"/>
        <path d="M 26 20 L 32 26 L 38 20" stroke="#8a6010" strokeWidth="0.8" fill="none"/>
        <path d="M 32 26 L 32 50" stroke="#8a6010" strokeWidth="0.5" fill="none" opacity="0.6"/>
      </svg>
    );
  }

  // Render via dotlottie web component. Using React.createElement to avoid JSX warnings on custom tag.
  return React.createElement('dotlottie-wc', {
    src: url,
    autoplay: true,
    loop: true,
    style: { width: size, height: size, borderRadius: '50%', flexShrink: 0, overflow: 'hidden' },
    onError: () => setFailed(true),
  });
};

const AishaMini = ({ lottieUrl }) => <AishaAvatar size={28} lottieUrl={lottieUrl}/>;

// ─── Product card rendered in chat ───
const ProductCard = ({ product, navigate, onAddToCart }) => {
  const mrp = product.price;
  const sellingPrice = product.discountPrice && product.discountPrice < mrp ? product.discountPrice : mrp;
  const hasDiscount = sellingPrice < mrp;
  const discountPct = hasDiscount ? Math.round(((mrp - sellingPrice) / mrp) * 100) : 0;
  const img = Array.isArray(product.images) && product.images[0] ? product.images[0] : null;

  return (
    <div style={{
      background: 'rgba(255,255,255,0.04)',
      border: '1px solid rgba(201,168,76,0.2)',
      borderRadius: 12, padding: 10, marginTop: 8,
      display: 'flex', gap: 10, cursor: 'pointer',
    }} onClick={() => navigate(`/product/${product._id}`)}>
      {img && (
        <img src={cldThumb(img)} alt={product.name}
          style={{ width: 60, height: 60, borderRadius: 8, objectFit: 'cover', flexShrink: 0 }}
          onError={(e) => { e.target.style.display = 'none'; }}/>
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ color: '#e8e8f5', fontSize: 12, fontWeight: 600, lineHeight: 1.3,
                      overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
          {product.name}
        </div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginTop: 4, flexWrap: 'wrap' }}>
          <span style={{ color: '#c9a84c', fontSize: 14, fontWeight: 800 }}>₹{sellingPrice}</span>
          {hasDiscount && (
            <>
              <span style={{ color: '#888', fontSize: 10, textDecoration: 'line-through' }}>₹{mrp}</span>
              <span style={{ color: '#fff', fontSize: 9, fontWeight: 700, background: '#c9184a', padding: '1px 5px', borderRadius: 3 }}>
                -{discountPct}%
              </span>
            </>
          )}
        </div>
        <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
          <button
            onClick={(e) => { e.stopPropagation(); onAddToCart(product); }}
            style={{ flex: 1, background: 'linear-gradient(135deg,#c9a84c,#f0d080)', color: '#0a0810',
                     border: 'none', borderRadius: 6, padding: '5px 8px', fontSize: 10, fontWeight: 700, cursor: 'pointer' }}>
            🛒 Add
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); navigate(`/product/${product._id}`); }}
            style={{ flex: 1, background: 'rgba(255,255,255,0.06)', color: '#e8e8f5',
                     border: '1px solid rgba(255,255,255,0.1)', borderRadius: 6, padding: '5px 8px',
                     fontSize: 10, fontWeight: 600, cursor: 'pointer' }}>
            👁 View
          </button>
        </div>
      </div>
    </div>
  );
};

const AIStyleChatbot = () => {
  const navigate = useNavigate();
  const addToCart = useCartStore(s => s.addItem);
  const [open, setOpen]       = useState(false);
  const [msgs, setMsgs]       = useState([]);
  const [input, setInput]     = useState('');
  const [loading, setLoading] = useState(false);
  const [lang, setLang]       = useState('en');
  const [products, setProducts] = useState([]);
  const [lottieUrl, setLottieUrl] = useState('');
  const bottomRef             = useRef(null);

  // v36 — kick off the lazy dotlottie load on mount (idle callback)
  useEffect(() => { ensureDotlottieLoaded(); }, []);

  useEffect(() => {
    if (open && msgs.length === 0) {
      setMsgs([{
        role: 'assistant',
        text: lang === 'hi'
          ? 'नमस्ते! मैं आयशा हूँ 💎 ग्लिट्ज़ एंड ग्लैम की स्टाइल असिस्टेंट। आप क्या ढूँढ रही हैं आज?'
          : "Hi, I'm Aisha 💎 Your style assistant at Glitz & Glam. What can I help you find today?"
      }]);
    }
  }, [open, lang, msgs.length]);

  // FAB coexistence (Apr25 v2): dispatch a global event when Aisha opens/closes
  // so WhatsAppButton can hide itself. Avoids overlap on mobile where both
  // buttons sit at bottom-right and stack visually. Custom event because we
  // don't want a global state lib for one boolean.
  useEffect(() => {
    const evt = new CustomEvent(open ? 'aisha:open' : 'aisha:close');
    window.dispatchEvent(evt);
  }, [open]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [msgs]);

  // v37 May10 — defer expensive API calls until user actually opens Aisha.
  // Previously these 3 calls fired on EVERY page mount: products (67.8KB),
  // aisha-avatar, gemini settings. ~99% of visitors never open the chat,
  // so 68KB + 3 round-trips were wasted on every first page load. Now they
  // fire on the first time the chat opens. Cached after that so re-open
  // is instant.
  const [hasFetchedOnce, setHasFetchedOnce] = useState(false);
  useEffect(() => {
    if (!open || hasFetchedOnce) return;
    setHasFetchedOnce(true);
    // Fetch products with full details (including discount price + images)
    fetch(`${API_BASE}/api/products?limit=100&status=active`)
      .then(r => r.json())
      .then(d => setProducts(d.products || []))
      .catch(() => {});
    // Fetch Aisha's Lottie avatar URL (admin-configurable from Settings)
    fetch(`${API_BASE}/api/settings/aisha-avatar`)
      .then(r => r.json())
      .then(d => { if (d.lottieUrl) setLottieUrl(d.lottieUrl); })
      .catch(() => {});
    // Fetch Gemini API key
    fetch(`${API_BASE}/api/settings/gemini`)
      .then(r => r.json())
      .then(d => {
        if (d.geminiKey) {
          localStorage.setItem('gemini_key', d.geminiKey);
          setGeminiKey(d.geminiKey);
        }
      })
      .catch(() => {});
  }, [open, hasFetchedOnce]);

  const [geminiKey, setGeminiKey] = useState(localStorage.getItem('gemini_key') || '');

  const getApiKey = () => geminiKey || localStorage.getItem('gemini_key');

  const handleAddToCart = (product) => {
    // FIX (Apr25): Aisha was passing the raw product object to cart, which
    // caused: (1) wrong price - cart used `product.price` (MRP) instead of
    // discountPrice, (2) image missing - cart expects `image` (singular)
    // but product has `images` (array). Normalize the same way ProductDetail
    // does, so cart and Aisha stay in sync.
    const sellingPrice =
      product.discountPrice && product.discountPrice < product.price
        ? product.discountPrice
        : product.price;

    addToCart({
      _id:        product._id,
      name:       product.name,
      price:      sellingPrice,                      // ✅ effective price
      image:      product.images?.[0] || null,       // ✅ first image
      barcodeId:  product.barcodeId,                  // for inventory tracking
      // size/color not selected from chat — let cart pick defaults
    });
    toast.success(`✨ Added ${product.name.slice(0, 30)}${product.name.length > 30 ? '…' : ''} to cart!`);
  };

  const handleNavigate = (url) => {
    setOpen(false);
    navigate(url);
  };

  const sendMessage = async (msg) => {
    const userMsg = (msg || input).trim();
    if (!userMsg) return;

    setInput('');
    setMsgs(m => [...m, { role: 'user', text: userMsg }]);
    setLoading(true);

    try {
      const key = getApiKey();
      if (!key) {
        setMsgs(m => [...m, { role: 'assistant', text: 'AI is still loading, please try in a moment!' }]);
        setLoading(false);
        return;
      }

      // Rich product context
      const productContext = products.map(p => {
        const mrp = p.price;
        const sellPrice = p.discountPrice && p.discountPrice < mrp ? p.discountPrice : mrp;
        const hasDiscount = sellPrice < mrp;
        const offText = hasDiscount ? ` [${Math.round((1 - sellPrice / mrp) * 100)}% OFF, save ₹${mrp - sellPrice}]` : '';
        return `- ID:${p._id} | ${p.name} | ₹${sellPrice}${hasDiscount ? ` (was ₹${mrp})${offText}` : ''} | ${p.category}${p.subcategory ? '/' + p.subcategory : ''} | Stock:${p.stock || 0}`;
      }).join('\n');

      // SEMANTIC TRAINING (Apr25):
      // Layer A — synonym dictionary (Hindi/English/Hinglish slang → canonical category)
      // Layer B — smart fallback instruction (suggest closest category if exact term not in stock)
      const synonymMap     = buildSynonymContext();
      const expansion      = expandQuery(userMsg);
      const expansionHint  = expansion.matchedCanonicals.length
        ? `\nCUSTOMER LIKELY MEANS: ${expansion.matchedCanonicals.join(', ')} (categories: ${expansion.categoryHints.join(', ') || 'any'})`
        : '';

      const systemPrompt = `You are Aisha, a warm and friendly style assistant for "Glitz & Glam by Jyoti" — a premium Indian women's fashion and artificial jewelry store (glitzandglambyjyoti.com).

AVAILABLE PRODUCTS (with actual selling prices and discount info):
${productContext || 'Jewelry, bangles, hair accessories, sarees, bags.'}

HINDI/ENGLISH SYNONYM REFERENCE (so you understand customer slang):
${synonymMap}
${expansionHint}

CRITICAL RULES:
1. Reply in the SAME language the customer uses (Hindi or English).
2. ALWAYS respond with valid JSON in this exact format:
{
  "message": "Your short friendly response (2-3 sentences with emojis)",
  "products": ["product_id_1", "product_id_2"]
}
3. Use the synonym reference above to understand colloquial terms. Example: "jhumka" = earrings, "chudi" = bangles, "haar" = necklace, "payal" = anklet.
4. SMART FALLBACK — If the customer asks for something we DON'T have in stock (e.g., "payal" but no anklets in product list, or "nath" but no nose rings):
   - Acknowledge warmly that the exact item isn't currently available.
   - Suggest 1-3 products from the CLOSEST matching category that they might love instead.
   - Briefly explain why your suggestion is similar (same style/occasion/look).
   - Example: "We don't have payal in stock right now, but these beautiful bangles would complete a similar traditional look ✨"
5. Use the exact ID values from the product list above. Never make up IDs.
6. Include 1-4 most relevant products. Empty array [] if truly nothing remotely related.
7. If customer asks about sales/offers, mention the discount percentage in your message.
8. For order tracking/returns, politely say they can check their Account page.

OUTPUT ONLY THE JSON OBJECT. No markdown fences, no extra text.`;

      const history = msgs.slice(-6).map(m => ({
        role: m.role === 'user' ? 'user' : 'model',
        parts: [{ text: typeof m.text === 'string' ? m.text : (m.text?.message || '') }]
      }));

      let data, lastErr;
      for (let attempt = 1; attempt <= 3; attempt++) {
        try {
          const res = await fetch(`${GEMINI_API}?key=${key}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              system_instruction: { parts: [{ text: systemPrompt }] },
              contents: [...history, { role: 'user', parts: [{ text: userMsg }] }]
            })
          });
          data = await res.json();
          if (data.error) {
            const em = data.error.message || '';
            if ((em.includes('overload') || em.includes('high demand') || em.includes('503') || em.includes('429')) && attempt < 3) {
              await new Promise(r => setTimeout(r, 2000 * attempt));
              continue;
            }
            throw new Error(em);
          }
          break;
        } catch (e) {
          lastErr = e;
          if (attempt < 3) await new Promise(r => setTimeout(r, 2000 * attempt));
        }
      }

      if (!data || data.error) throw new Error(lastErr?.message || 'Failed');

      const rawText = data.candidates?.[0]?.content?.parts?.[0]?.text || '';
      let parsed;
      try {
        const clean = rawText.replace(/```json|```/g, '').trim();
        parsed = JSON.parse(clean);
      } catch {
        parsed = { message: rawText, products: [] };
      }

      const foundProducts = (parsed.products || [])
        .map(id => products.find(p => p._id === id))
        .filter(Boolean)
        .slice(0, 4);

      setMsgs(m => [...m, {
        role: 'assistant',
        text: parsed.message || 'Let me help you find something beautiful!',
        products: foundProducts,
      }]);
    } catch (e) {
      const msg = e.message || '';
      const userFriendly = (msg.includes('high demand') || msg.includes('overload') || msg.includes('503'))
        ? '⏳ I\'m a bit overloaded right now. Please try again in a moment!'
        : (msg.includes('quota') || msg.includes('429'))
        ? '⏱ I\'ve hit my daily limit. Please try tomorrow!'
        : 'Sorry, something went wrong. Please try again! 🙏';
      setMsgs(m => [...m, { role: 'assistant', text: userFriendly }]);
    } finally {
      setLoading(false);
    }
  };

  const formatText = (text) => {
    if (typeof text !== 'string') return text;
    return text.split('\n').map((line, i) => (
      <div key={i} style={{ marginBottom: line ? 3 : 0 }}>{line}</div>
    ));
  };

  if (!open) return (
    <button style={S.fab} onClick={() => setOpen(true)} aria-label="Chat with Aisha">
      <AishaAvatar size={48} lottieUrl={lottieUrl}/>
      <span style={S.pulseRing}/>
    </button>
  );

  return (
    <div style={S.window}>
      <div style={S.header}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <AishaAvatar size={40} lottieUrl={lottieUrl}/>
          <div>
            <div style={S.botName}>Aisha</div>
            <div style={S.botStatus}>● Online · Style Assistant</div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <button style={{ ...S.langBtn, ...(lang === 'en' ? S.langActive : {}) }} onClick={() => setLang('en')}>EN</button>
          <button style={{ ...S.langBtn, ...(lang === 'hi' ? S.langActive : {}) }} onClick={() => setLang('hi')}>हि</button>
          <button style={S.closeBtn} onClick={() => setOpen(false)}>✕</button>
        </div>
      </div>

      <div style={S.messages}>
        {msgs.map((m, i) => (
          <div key={i} style={{ display: 'flex', justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start', marginBottom: 10, alignItems: 'flex-start' }}>
            {m.role === 'assistant' && <div style={{ marginRight: 6 }}><AishaMini lottieUrl={lottieUrl}/></div>}
            <div style={{ ...S.bubble, ...(m.role === 'user' ? S.userBubble : S.botBubble), maxWidth: '82%' }}>
              {formatText(m.text)}
              {m.products && m.products.length > 0 && m.products.map(p => (
                <ProductCard
                  key={p._id}
                  product={p}
                  navigate={handleNavigate}
                  onAddToCart={handleAddToCart}
                />
              ))}
            </div>
          </div>
        ))}
        {loading && (
          <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: 10, alignItems: 'flex-start' }}>
            <div style={{ marginRight: 6 }}><AishaMini lottieUrl={lottieUrl}/></div>
            <div style={{ ...S.bubble, ...S.botBubble }}>
              <span style={S.dot}/><span style={S.dot}/><span style={S.dot}/>
            </div>
          </div>
        )}
        <div ref={bottomRef}/>
      </div>

      {msgs.length <= 1 && (
        <div style={S.quickRow}>
          {QUICK_QUESTIONS.map((q, i) => (
            <button key={i} style={S.quickBtn} onClick={() => sendMessage(lang === 'hi' ? q.hi : q.en)}>
              {lang === 'hi' ? q.hi : q.en}
            </button>
          ))}
        </div>
      )}

      <div style={S.inputRow}>
        <input
          style={S.input}
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && !loading && sendMessage()}
          placeholder={lang === 'hi' ? 'अपना सवाल लिखें...' : 'Ask me anything...'}
          disabled={loading}
        />
        <button style={S.sendBtn} onClick={() => sendMessage()} disabled={loading}>
          {loading ? '...' : '→'}
        </button>
      </div>
    </div>
  );
};

const S = {
  fab: {
    position: 'fixed', bottom: 96, right: 24, width: 60, height: 60,
    background: 'transparent', border: 'none', borderRadius: '50%',
    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
    boxShadow: '0 4px 24px rgba(201,168,76,0.5)',
    zIndex: 9990, padding: 0, overflow: 'visible',
  },
  pulseRing: {
    position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
    borderRadius: '50%', border: '2px solid rgba(201,168,76,0.6)',
    animation: 'aishaPulse 2.2s ease-out infinite',
    pointerEvents: 'none',
  },
  window: {
    position: 'fixed', bottom: 16, right: 16, left: 16,
    width: 'auto', maxWidth: 400, height: '82vh', maxHeight: 600,
    background: '#0e0b18', border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 20, boxShadow: '0 20px 60px rgba(0,0,0,0.6)',
    zIndex: 9990, display: 'flex', flexDirection: 'column', overflow: 'hidden',
  },
  header: {
    padding: '14px 16px', borderBottom: '1px solid rgba(255,255,255,0.06)',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    background: 'linear-gradient(135deg, rgba(201,168,76,0.08), rgba(240,208,128,0.03))',
  },
  botName: { color: '#c9a84c', fontSize: 15, fontWeight: 700, fontFamily: 'Georgia, serif', letterSpacing: 0.3 },
  botStatus: { color: '#888', fontSize: 10 },
  langBtn: { background: 'transparent', border: '1px solid rgba(255,255,255,0.1)', color: '#888', padding: '4px 8px', borderRadius: 6, fontSize: 11, cursor: 'pointer' },
  langActive: { background: '#c9a84c', color: '#0a0810', borderColor: '#c9a84c' },
  closeBtn: { background: 'transparent', border: 'none', color: '#888', fontSize: 16, cursor: 'pointer', padding: '4px 8px' },
  messages: { flex: 1, overflowY: 'auto', padding: 14, background: '#0a0810' },
  bubble: { padding: '9px 13px', borderRadius: 14, fontSize: 13, lineHeight: 1.5, wordBreak: 'break-word' },
  userBubble: { background: 'linear-gradient(135deg,#c9a84c,#f0d080)', color: '#0a0810', borderBottomRightRadius: 4 },
  botBubble: { background: 'rgba(255,255,255,0.05)', color: '#e8e8f5', borderBottomLeftRadius: 4 },
  dot: { display: 'inline-block', width: 6, height: 6, borderRadius: '50%', background: '#c9a84c', margin: '0 2px', animation: 'aishaDot 1.2s infinite' },
  quickRow: { padding: '8px 12px', display: 'flex', gap: 6, overflowX: 'auto', borderTop: '1px solid rgba(255,255,255,0.04)' },
  quickBtn: { whiteSpace: 'nowrap', background: 'rgba(201,168,76,0.1)', border: '1px solid rgba(201,168,76,0.25)', color: '#c9a84c', padding: '6px 12px', borderRadius: 16, fontSize: 11, cursor: 'pointer' },
  inputRow: { padding: 12, borderTop: '1px solid rgba(255,255,255,0.06)', display: 'flex', gap: 8, background: '#0a0810' },
  input: { flex: 1, background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 20, padding: '10px 14px', color: '#e8e8f5', fontSize: 13, outline: 'none' },
  sendBtn: { background: 'linear-gradient(135deg,#c9a84c,#f0d080)', border: 'none', color: '#0a0810', width: 40, height: 40, borderRadius: '50%', fontSize: 18, fontWeight: 700, cursor: 'pointer' },
};

// Inject global keyframes once
if (typeof document !== 'undefined' && !document.getElementById('aisha-keyframes')) {
  const styleEl = document.createElement('style');
  styleEl.id = 'aisha-keyframes';
  styleEl.textContent = `
    @keyframes aishaPulse { 0% { transform: scale(1); opacity: 0.8; } 100% { transform: scale(1.35); opacity: 0; } }
    @keyframes aishaDot { 0%,80%,100% { opacity: 0.3; } 40% { opacity: 1; } }
  `;
  document.head.appendChild(styleEl);
}

export default AIStyleChatbot;
